import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { FriendlySettlementTable, SettlementChargeLinesTable } from '../display';
import { SettlementLinesFiltersSection } from '../interaction/SettlementLinesFiltersSection';
import { COURIER_INVOICES_BASE_PATH } from '../../../constants';
import type { CourierSettlementLinesFilters } from '../../../models/settlementLinesFilters';
import { countActiveSettlementLinesFilters } from '../../../models/settlementLinesFilters';
import {
  useCourierSettlementDetail,
  useCourierSettlementExceptions,
  useCourierSettlementLines,
} from '../../hooks/useCourierSettlementQueries';
import { useRecalculateCourierSettlementPendingLines } from '../../hooks/useRecalculateCourierSettlementPendingLines';
import { ErrorState } from '../../../List/components/feedback/ErrorState';
import { LoadingState } from '../../../List/components/feedback/LoadingState';
import { usePermissions } from '@shared/hooks/usePermissions';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/utils/utils';
import { RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SettlementDetailMainProps {
  settlementId: number;
}

export const SettlementDetailMain = ({ settlementId }: SettlementDetailMainProps) => {
  const { t } = useTranslation('courierInvoicesModule');
  const { getTeamType } = usePermissions();
  const hasTeamData = getTeamType() === 'tenant_3pl';

  const [linesFilters, setLinesFilters] = useState<CourierSettlementLinesFilters>({});

  const detailQuery = useCourierSettlementDetail(settlementId);
  const linesQuery = useCourierSettlementLines(settlementId, linesFilters);
  const exceptionsQuery = useCourierSettlementExceptions(settlementId);

  const activeLinesFiltersCount = countActiveSettlementLinesFilters(linesFilters);

  const invoiceIdFromDetail = useMemo(() => {
    const d = detailQuery.data;
    if (!d) return null;
    const direct = d.courier_invoice_id;
    if (typeof direct === 'number') return direct;
    if (typeof direct === 'string') return Number(direct) || null;
    const nested = d.courier_invoice as Record<string, unknown> | undefined;
    if (nested && nested.id !== undefined) {
      return Number(nested.id);
    }
    return null;
  }, [detailQuery.data]);

  const recalculatePendingMutation = useRecalculateCourierSettlementPendingLines({
    settlementId,
    courierInvoiceId: invoiceIdFromDetail,
  });

  const breadcrumbs = useMemo(() => {
    const items: { label: string; path?: string }[] = [
      { label: t('breadcrumbs.courier_invoices'), path: COURIER_INVOICES_BASE_PATH },
    ];
    if (invoiceIdFromDetail != null && invoiceIdFromDetail > 0) {
      items.push({
        label: t('detail.title', { id: invoiceIdFromDetail }),
        path: `${COURIER_INVOICES_BASE_PATH}/${invoiceIdFromDetail}`,
      });
    }
    items.push({ label: t('settlement.title', { id: settlementId }) });
    return items;
  }, [t, settlementId, invoiceIdFromDetail]);

  usePageNavigation({ breadcrumbs });

  if (detailQuery.isLoading && !detailQuery.data) {
    return (
      <div className="px-6 pt-4">
        <LoadingState message={t('loading.detail')} className="min-h-[280px]" />
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="px-6 pt-4">
        <ErrorState
          error={detailQuery.error ?? new Error(t('errors.unexpected'))}
          onRetry={() => void detailQuery.refetch()}
        />
      </div>
    );
  }

  const lines = linesQuery.lines;
  const exceptions = exceptionsQuery.data ?? [];

  const handleSearchApply = (query: string) => {
    setLinesFilters((prev) => {
      const next = { ...prev };
      if (!query) delete next.search;
      else next.search = query.slice(0, 255);
      return next;
    });
  };

  return (
    <div className="px-6 pt-4 pb-8 space-y-8">
      {linesQuery.isError ? (
        <ErrorState error={linesQuery.error} onRetry={() => void linesQuery.refetch()} />
      ) : linesQuery.isPending && lines.length === 0 ? (
        <LoadingState message={t('loading.detail')} className="min-h-[200px]" />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex flex-wrap items-center gap-3 min-w-0 flex-1">
              <SettlementLinesFiltersSection
                settlementId={settlementId}
                handleSearch={handleSearchApply}
                searchTerm={linesFilters.search ?? ''}
                filters={linesFilters}
                onFiltersChange={setLinesFilters}
                hasTeamData={hasTeamData}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={recalculatePendingMutation.isPending}
              onClick={() => recalculatePendingMutation.mutate()}
              className="shrink-0 border-input bg-background transition-colors"
              data-testid="settlement-recalculate-pending-lines"
            >
              <RefreshCw
                className={cn('h-4 w-4 mr-2', recalculatePendingMutation.isPending && 'animate-spin')}
                aria-hidden
              />
              {t('settlement.recalculate_pending.button')}
            </Button>
          </div>

          <SettlementChargeLinesTable
            rows={lines}
            fetchNextPage={() => void linesQuery.fetchNextPage()}
            hasNextPage={linesQuery.hasNextPage ?? false}
            isFetchingNextPage={linesQuery.isFetchingNextPage}
            isFetching={linesQuery.isFetching && !linesQuery.isFetchingNextPage}
            hasActiveFilters={activeLinesFiltersCount > 0}
          />
        </>
      )}

      {exceptions.length > 0 ? (
        <div className="space-y-3">
          {exceptionsQuery.isError ? (
            <ErrorState error={exceptionsQuery.error} onRetry={() => void exceptionsQuery.refetch()} />
          ) : exceptionsQuery.isLoading ? (
            <LoadingState message={t('loading.detail')} className="min-h-[120px]" />
          ) : (
            <FriendlySettlementTable rows={exceptions} variant="exceptions" />
          )}
        </div>
      ) : null}
    </div>
  );
};
